import * as THREE from 'three';
import { random } from '../look/noise.js';
import { mergeBaked } from './tree-shapes.js';

/**
 * The shapes of grass tufts, apart from the renderer (nothing here but three and the noise): the blade
 * atlas drawn in code, a tuft's crossed cards. grass.ts draws them as one instanced mesh; a package's
 * conversion bakes them into one plain mesh (`bakedTufts`), the same cards in the same places.
 */

/**
 * A texture of grass blades, `size` pixels square, from a seed: each blade
 * rooted along the bottom, tapering and bending to its tip, dark green at the
 * root to a lighter, yellower green at the tip; clear between them. Row 0 is
 * the bottom, as a texture's v runs.
 */
export function bladeAtlas(
  size = 128,
  seed = 7,
  colours: { root: THREE.ColorRepresentation; tip: THREE.ColorRepresentation } = {
    root: 0x3b5226,
    tip: 0x93a05a,
  },
): THREE.DataTexture {
  const next = random(seed);
  const data = new Uint8Array(size * size * 4);
  const root = new THREE.Color(colours.root);
  const tip = new THREE.Color(colours.tip);
  const c = new THREE.Color();
  const blades = Math.round(size / 6);
  for (let b = 0; b < blades; b++) {
    const x0 = size * (0.08 + 0.84 * next());
    const lean = (next() - 0.5) * size * 0.5;
    const tall = size * (0.55 + 0.45 * next());
    const width = size * (0.02 + 0.018 * next());
    const shade = 0.75 + 0.35 * next();
    for (let y = 0; y < tall; y++) {
      const t = y / tall;
      // Bending more towards the tip.
      const cx = x0 + lean * t * t;
      const half = Math.max(0.35, width * (1 - t) ** 0.8);
      c.copy(root).lerp(tip, t).multiplyScalar(shade);
      for (let x = Math.floor(cx - half); x <= Math.ceil(cx + half); x++) {
        if (x < 0 || x >= size) continue;
        const i = (y * size + x) * 4;
        data[i] = Math.round(Math.min(1, c.r) * 255);
        data[i + 1] = Math.round(Math.min(1, c.g) * 255);
        data[i + 2] = Math.round(Math.min(1, c.b) * 255);
        data[i + 3] = 255;
      }
    }
  }
  const texture = new THREE.DataTexture(data, size, size);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.generateMipmaps = true;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.needsUpdate = true;
  return texture;
}

/**
 * One tuft: three cards 1 m wide and 1 m high, crossed at 60°, standing on
 * y = 0. Their normals point up, so a tuft is lit as the ground under it is
 * and no card goes dark edge-on to the sun.
 */
export function tuftGeometry(): THREE.BufferGeometry {
  const positions: number[] = [];
  const uvs: number[] = [];
  const index: number[] = [];
  for (let k = 0; k < 3; k++) {
    const a = (k * Math.PI) / 3;
    const dx = Math.cos(a) * 0.5;
    const dz = Math.sin(a) * 0.5;
    const base = positions.length / 3;
    positions.push(-dx, 0, -dz, dx, 0, dz, dx, 1, dz, -dx, 1, -dz);
    uvs.push(0, 0, 1, 0, 1, 1, 0, 1);
    index.push(base, base + 1, base + 2, base, base + 2, base + 3);
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setAttribute(
    'normal',
    new THREE.Float32BufferAttribute(
      Array.from({ length: positions.length / 3 }, () => [0, 1, 0]).flat(),
      3,
    ),
  );
  geometry.setIndex(index);
  return geometry;
}

/** Where a tuft stands, how high and wide (metres), its turn (degrees) and its shade (1 as drawn). */
export type Tuft = {
  readonly pos: readonly [number, number, number];
  readonly height: number;
  readonly width: number;
  readonly turn: number;
  readonly shade: number;
};

/**
 * Tufts as one plain geometry for a package (part 3b): each tuft's cards placed as `grassTufts`
 * places its instance (its position, turn, and its width and height as the scale), with its shade as
 * the vertex colour. The kit gives it the blade atlas.
 */
export function bakedTufts(tufts: readonly Tuft[]): THREE.BufferGeometry {
  const card = tuftGeometry();
  const matrix = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const up = new THREE.Vector3(0, 1, 0);
  const parts = tufts.map((tuft) => {
    q.setFromAxisAngle(up, THREE.MathUtils.degToRad(tuft.turn));
    matrix.compose(
      new THREE.Vector3(...tuft.pos),
      q,
      new THREE.Vector3(tuft.width, tuft.height, tuft.width),
    );
    const part = card.clone().applyMatrix4(matrix);
    const count = part.getAttribute('position').count;
    part.setAttribute(
      'color',
      new THREE.Float32BufferAttribute(
        Array.from({ length: count }, () => [tuft.shade, tuft.shade, tuft.shade]).flat(),
        3,
      ),
    );
    return part;
  });
  return mergeBaked(parts);
}
